import { zodResolver } from '@hookform/resolvers/zod';
import {
  Bell,
  Building2,
  Calendar,
  FileText,
  Globe,
  Helicopter,
  Plus,
  ShieldCheck,
  Users,
} from 'lucide-react';
import { DateTime } from 'luxon';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import type { z } from 'zod';
import {
  PayloadApiError,
  useCreateCustomer,
  useCustomer,
  useObjectCategories,
  useUpdateCustomer,
} from '@/core/api';
import { useAuth } from '@/core/auth';
import { useIntl } from '@/core/i18n/use-intl';
import { ObjectsOverview } from '@/features/shared/objects';
import { UserManagement } from '@/features/shared/users';
import { CustomerDetailsFields } from '@/shared/components/customer/customer-details-fields';
import { CustomerQuotaCard } from '@/shared/components/customer/customer-quota-card';
import { ObjectReminderSettingsCard } from '@/shared/components/customer/object-reminder-settings-card';
import { DetailCard, DetailPageLayout, InfoCard, TabNav } from '@/shared/components/layout';
import {
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
} from '@/shared/components/ui';
import { useUnsavedChangesGuard } from '@/shared/hooks/use-unsaved-changes-guard';
import {
  DEFAULT_CUSTOMER_GROUP_KEYS,
  HELI_PROVIDER_GROUP_KEY,
  isGmnAdminGroup,
} from '@/shared/utils/permissions';
import { customerFormSchema } from '../schema';
import type { CustomerFormValues } from '../types';

type ManageCustomerProps = {
  variant?: 'customer' | 'heli-provider';
};

export default function ManageCustomer({ variant = 'customer' }: ManageCustomerProps) {
  const { t } = useIntl();
  const { user: currentUser } = useAuth();
  const createCustomer = useCreateCustomer();
  const updateCustomer = useUpdateCustomer();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const isEditMode = !!id;
  const isHeliProviderView = variant === 'heli-provider';
  const basePath = isHeliProviderView
    ? '/gmn/customer-management/helicopter-service-providers'
    : '/gmn/customer-management/customers';

  const { data: customer } = useCustomer(id || '');
  const isGmnAdmin = isGmnAdminGroup(currentUser);
  const customerType: CustomerFormValues['customerType'] =
    (isEditMode
      ? ((customer?.customerType as CustomerFormValues['customerType']) ?? variant)
      : variant) ?? 'customer';
  const isHeliProviderCustomer = customerType === 'heli-provider';
  const allowedGroupKeys = isHeliProviderCustomer
    ? [HELI_PROVIDER_GROUP_KEY]
    : DEFAULT_CUSTOMER_GROUP_KEYS.filter((key) => key !== HELI_PROVIDER_GROUP_KEY);
  const newTitle = isHeliProviderCustomer
    ? t('customers.newHelicopterServiceProvider')
    : t('customers.newCustomer');
  const createNewHint = isHeliProviderCustomer
    ? t('customers.createNewHelicopterServiceProviderHint')
    : t('customers.createNewCustomerHint');
  const createHint = isHeliProviderCustomer
    ? t('customers.createHelicopterServiceProviderHint')
    : t('customers.createCustomerHint');
  const [usage, setUsage] = useState({ userCount: 0, inviteCount: 0 });
  const [userQuotaInput, setUserQuotaInput] = useState('50');
  const [isInviteDialogOpen, setIsInviteDialogOpen] = useState(false);
  const initialValuesRef = useRef<CustomerFormValues | null>(null);
  const initialQuotaInputRef = useRef<string>('50');

  const { data: helicopterCategories } = useObjectCategories({
    where: {
      or: [{ name: { equals: 'Helikopter' } }, { name: { equals: 'Rettungshubschrauber' } }],
    },
    limit: 20,
    disabled: !isEditMode || !isHeliProviderCustomer,
  });
  const helicopterCategoryIds = useMemo(
    () => (helicopterCategories?.docs ?? []).map((category) => String(category.id)),
    [helicopterCategories?.docs]
  );
  const helicopterOverviewWhere = useMemo<Record<string, unknown>[]>(() => {
    if (!isEditMode || !isHeliProviderCustomer || !id) return [];
    if (helicopterCategoryIds.length === 0) {
      return [{ id: { equals: '__none__' } }];
    }
    return [
      {
        assignedHelicopterServiceProvider: {
          equals: id,
        },
      },
      {
        category: {
          in: helicopterCategoryIds,
        },
      },
    ];
  }, [helicopterCategoryIds, id, isEditMode, isHeliProviderCustomer]);

  const form = useForm<z.input<typeof customerFormSchema>, any, CustomerFormValues>({
    resolver: zodResolver(customerFormSchema),
    defaultValues: {
      name: '',
      companyType: '',
      industry: '',
      website: '',
      userQuota: 50,
      customerType: isHeliProviderView ? 'heli-provider' : 'customer',
      objectDigestEnabled: false,
      objectDigestRecipients: [],
      objectDigestInterval: 6,
    },
  });
  const userQuotaValue = Number(form.watch('userQuota') ?? 0);
  const totalUsage = usage.userCount + usage.inviteCount;
  const quotaForUsage = isEditMode ? (customer?.userQuota ?? userQuotaValue) : userQuotaValue;
  const handleUsageChange = useCallback((nextUsage: typeof usage) => {
    setUsage(nextUsage);
  }, []);

  const watchedValues = useWatch({ control: form.control });
  const recipientsEqual = useCallback((a?: string[], b?: string[]) => {
    const left = a ?? [];
    const right = b ?? [];
    if (left.length !== right.length) return false;
    const leftSorted = [...left].sort();
    const rightSorted = [...right].sort();
    return leftSorted.every((value, index) => value === rightSorted[index]);
  }, []);
  const hasPendingChanges = useMemo(() => {
    if (!initialValuesRef.current) return false;
    const initial = initialValuesRef.current;
    return (
      watchedValues?.name !== initial.name ||
      watchedValues?.companyType !== initial.companyType ||
      watchedValues?.industry !== initial.industry ||
      watchedValues?.website !== initial.website ||
      watchedValues?.userQuota !== initial.userQuota ||
      watchedValues?.objectDigestEnabled !== initial.objectDigestEnabled ||
      watchedValues?.objectDigestInterval !== initial.objectDigestInterval ||
      !recipientsEqual(watchedValues?.objectDigestRecipients, initial.objectDigestRecipients)
    );
  }, [recipientsEqual, watchedValues]);

  const lastSyncedCustomerRef = useRef<string | null>(null);
  useEffect(() => {
    if (!isEditMode || !customer) {
      return;
    }

    const signature = `${customer.id}:${customer.updatedAt ?? ''}:${customer.userQuota ?? ''}`;
    if (lastSyncedCustomerRef.current === signature) {
      return;
    }

    const nextQuota = customer.userQuota || 50;
    const nextValues: CustomerFormValues = {
      name: customer.name,
      companyType: customer.companyType || '',
      industry: customer.industry || '',
      website: customer.website || '',
      userQuota: nextQuota,
      customerType: (customer.customerType as CustomerFormValues['customerType']) ?? 'customer',
      objectDigestEnabled: customer.objectDigestEnabled ?? false,
      objectDigestRecipients: (customer.objectDigestRecipients ?? []) as string[],
      objectDigestInterval: customer.objectDigestInterval ?? 6,
    };
    form.reset(nextValues);
    setUserQuotaInput(String(nextQuota));
    initialValuesRef.current = nextValues;
    initialQuotaInputRef.current = String(nextQuota);
    lastSyncedCustomerRef.current = signature;
  }, [isEditMode, customer, form]);

  useEffect(() => {
    if (isEditMode) return;
    const currentValue = form.getValues('userQuota');
    if (typeof currentValue === 'number' && !Number.isNaN(currentValue)) {
      setUserQuotaInput(String(currentValue));
    } else {
      setUserQuotaInput('');
    }
    if (!initialValuesRef.current) {
      const values = form.getValues();
      const normalizedQuota =
        typeof values.userQuota === 'number'
          ? values.userQuota
          : Number(values.userQuota ?? 50) || 50;
      const normalizedInterval =
        typeof values.objectDigestInterval === 'number'
          ? values.objectDigestInterval
          : Number(values.objectDigestInterval ?? 6) || 6;

      initialValuesRef.current = {
        name: values.name ?? '',
        companyType: values.companyType ?? '',
        industry: values.industry ?? '',
        website: values.website ?? '',
        userQuota: normalizedQuota,
        customerType: (values.customerType as CustomerFormValues['customerType']) ?? 'customer',
        objectDigestEnabled: values.objectDigestEnabled ?? false,
        objectDigestRecipients: values.objectDigestRecipients ?? [],
        objectDigestInterval: normalizedInterval,
      };
      initialQuotaInputRef.current = String(normalizedQuota);
    }
  }, [isEditMode, form]);

  const discardChanges = () => {
    if (initialValuesRef.current) {
      form.reset(initialValuesRef.current);
    }
    setUserQuotaInput(initialQuotaInputRef.current);
  };

  const { dialog: unsavedDialog, allowNextNavigation } = useUnsavedChangesGuard({
    when: hasPendingChanges,
    onDiscard: discardChanges,
  });

  const handleQuotaValidationErrors = (error: unknown) => {
    if (!(error instanceof PayloadApiError)) return false;
    const messages =
      error.response?.errors?.map((err) => {
        if (typeof err === 'object' && err && 'message' in err) {
          return String((err as { message: string }).message);
        }
        return '';
      }) ?? [];

    if (messages.includes('CustomerQuotaBelowMinimum')) {
      form.setError('userQuota', {
        type: 'manual',
        message: t('errors.quotaBelowMinimum'),
      });
      return true;
    }

    if (messages.includes('CustomerQuotaBelowActiveUsers')) {
      form.setError('userQuota', {
        type: 'manual',
        message: t('errors.quotaBelowActiveUsers'),
      });
      return true;
    }

    return false;
  };

  const handleSubmit = async (data: CustomerFormValues) => {
    if (typeof data.userQuota !== 'number' || Number.isNaN(data.userQuota) || data.userQuota < 5) {
      form.setError('userQuota', {
        type: 'manual',
        message: t('errors.quotaBelowMinimum'),
      });
      return;
    }

    if (data.objectDigestEnabled && (data.objectDigestRecipients ?? []).length === 0) {
      form.setError('objectDigestRecipients', {
        type: 'manual',
        message: t('customer.objectDigest.recipientsRequired'),
      });
      return;
    }
    if (
      data.objectDigestEnabled &&
      (typeof data.objectDigestInterval !== 'number' ||
        Number.isNaN(data.objectDigestInterval) ||
        data.objectDigestInterval < 1 ||
        data.objectDigestInterval > 24)
    ) {
      form.setError('objectDigestInterval', {
        type: 'manual',
        message: t('customer.objectDigest.intervalInvalid'),
      });
      return;
    }

    if (!isGmnAdmin) return;

    const payload = { ...data, customerType };

    try {
      if (isEditMode) {
        await updateCustomer.mutateAsync({ id, data: payload });
        toast.success(t('customers.customerUpdatedSuccessfully'));
      } else {
        const createdCustomer = await createCustomer.mutateAsync(payload);
        toast.success(t('customers.customerCreatedSuccessfully'));
        allowNextNavigation();
        navigate(`${basePath}/${createdCustomer.doc.id}`);
      }
    } catch (error) {
      const handled = handleQuotaValidationErrors(error);
      if (!handled) {
        toast.error(t('customers.errorSavingCustomer'));
      }
    }
  };

  // Build meta items for header
  const metaItems =
    isEditMode && customer
      ? [
          ...(customer.industry ? [{ icon: Building2, label: customer.industry }] : []),
          ...(customer.website
            ? [
                {
                  icon: Globe,
                  label: customer.website.replace('https://', ''),
                  href: customer.website,
                },
              ]
            : []),
          ...(customer.createdAt
            ? [
                {
                  icon: Calendar,
                  label: `${t('common.since')} ${DateTime.fromISO(customer.createdAt).toFormat('dd.MM.yyyy')}`,
                },
              ]
            : []),
        ]
      : undefined;

  // Avatar content with initials
  const avatarContent =
    isEditMode && customer ? (
      <span className="text-3xl font-bold">{customer.name.substring(0, 2).toUpperCase()}</span>
    ) : undefined;

  const rawTab = location.pathname.split('/').pop() || 'general';
  const normalizedTab = rawTab === id ? 'general' : rawTab;
  const currentTab = isEditMode ? normalizedTab : 'general';
  const tabs = [
    { value: 'general', label: t('common.general'), icon: FileText },
    { value: 'users', label: t('users.users'), icon: Users },
    ...(isHeliProviderCustomer
      ? [{ value: 'helicopters', label: t('customers.helicoptersTab'), icon: Helicopter }]
      : []),
    { value: 'notifications', label: t('customer.objectDigest.title'), icon: Bell },
  ];
  const isGeneralTab = currentTab === 'general';
  const isHelicoptersTab = currentTab === 'helicopters';
  const isNotificationsTab = currentTab === 'notifications';
  const isUsersTab = currentTab === 'users';
  const isPendingSave = isEditMode ? updateCustomer.isPending : createCustomer.isPending;

  return (
    <DetailPageLayout
      containerProps={{ animation: 'slide-right' }}
      headerProps={{
        backHref: basePath,
        backLabel: t('customers.backToOverview'),
        title: isEditMode && customer ? customer.name : newTitle,
        avatarContent,
        isCreateMode: !isEditMode,
        metaItems,
        subtitle: !isEditMode ? createNewHint : undefined,
        secondaryAction: {
          label: t('common.discard'),
          onClick: () => navigate(basePath),
        },
        primaryAction: {
          label: isEditMode
            ? isPendingSave
              ? t('common.updating')
              : t('common.update')
            : isPendingSave
              ? t('common.creating')
              : t('common.create'),
          onClick: form.handleSubmit(handleSubmit),
          disabled: isPendingSave || !isGmnAdmin,
          loading: isPendingSave,
        },
      }}
    >
      {isEditMode && <TabNav tabs={tabs} basePath={`${basePath}/${id}`} variant="underline" />}

      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)}>
          {(!isEditMode || isGeneralTab) && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Main Content Column (2/3) */}
              <div className="lg:col-span-2 space-y-8">
                {/* General Info Card */}
                <DetailCard icon={Building2} title={t('customers.customerDetails')}>
                  <CustomerDetailsFields
                    control={form.control}
                    disabled={!isGmnAdmin}
                    labels={{
                      name: t('common.name'),
                      companyType: t('customers.companyType'),
                      industry: t('customers.industry'),
                      website: t('customers.website'),
                    }}
                    placeholders={{
                      name: t('customers.companyNamePlaceholder'),
                      companyType: t('customers.companyTypePlaceholder'),
                      industry: t('customers.industryPlaceholder'),
                      website: 'https://example.com',
                    }}
                    websiteWithIcon
                  />
                </DetailCard>
              </div>

              {/* Sidebar Column (1/3) */}
              <div className="space-y-6">
                {/* Quota Card */}
                <CustomerQuotaCard
                  icon={ShieldCheck}
                  title={t('customers.licenseAndQuota')}
                  usageLabel={t('customers.userLicenses')}
                  totalUsage={totalUsage}
                  quota={quotaForUsage}
                  quotaControl={
                    <FormField
                      control={form.control}
                      name="userQuota"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel required>{t('customers.maxUserCount')}</FormLabel>
                          <FormControl>
                            <Input
                              {...field}
                              type="number"
                              disabled={!isGmnAdmin}
                              value={userQuotaInput}
                              onChange={(event) => {
                                const nextValue = event.target.value;
                                setUserQuotaInput(nextValue);
                                if (nextValue === '') {
                                  field.onChange(undefined);
                                  return;
                                }
                                const parsed = Number(nextValue);
                                field.onChange(Number.isNaN(parsed) ? undefined : parsed);
                              }}
                              onBlur={(event) => {
                                const value = event.target.value;
                                setUserQuotaInput(value);
                                if (value === '') {
                                  field.onChange(undefined);
                                  form.setError('userQuota', {
                                    type: 'manual',
                                    message: t('errors.quotaBelowMinimum'),
                                  });
                                  return;
                                }
                                const parsedValue = Number(value);
                                if (Number.isNaN(parsedValue)) {
                                  field.onChange(undefined);
                                  form.setError('userQuota', {
                                    type: 'manual',
                                    message: t('errors.quotaBelowMinimum'),
                                  });
                                  return;
                                }
                                field.onChange(parsedValue);
                                if (parsedValue < 5) {
                                  form.setError('userQuota', {
                                    type: 'manual',
                                    message: t('errors.quotaBelowMinimum'),
                                  });
                                } else {
                                  form.clearErrors('userQuota');
                                }
                              }}
                            />
                          </FormControl>
                          <p className="text-xs text-muted-foreground">
                            {t('customers.userQuotaHelper')}
                          </p>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  }
                />

                {/* Info Card - Only in Create Mode */}
                {!isEditMode && (
                  <InfoCard title={t('common.hint')} variant="info">
                    <p>{createHint}</p>
                  </InfoCard>
                )}
              </div>
            </div>
          )}

          {isEditMode && isNotificationsTab && (
            <ObjectReminderSettingsCard
              control={form.control}
              disabled={!isGmnAdmin}
              customerId={id}
              lastSentAt={customer?.objectDigestLastSentAt}
              enabledAt={customer?.objectDigestEnabledAt}
              customerType={customer?.customerType}
            />
          )}
        </form>
      </Form>

      {isEditMode && isHelicoptersTab && isHeliProviderCustomer && (
        <div className="mt-8">
          <ObjectsOverview
            embedded
            hidePageTitle
            tableId={`heli-provider-assigned-helicopters-${id}`}
            forcedWhereAnd={helicopterOverviewWhere}
            forceHelicopterTableView
          />
        </div>
      )}

      {/* Users Card - Only in Edit Mode - Full Width */}
      {isEditMode && (
        <DetailCard
          icon={Users}
          title={t('users.users')}
          noPadding
          className={isUsersTab ? 'mt-8' : 'mt-8 hidden'}
          headerAction={
            isGmnAdmin && (
              <Button variant="outline" size="sm" onClick={() => setIsInviteDialogOpen(true)}>
                <Plus className="size-4 mr-2" />
                {t('users.inviteUser')}
              </Button>
            )
          }
        >
          <UserManagement
            allowedGroupKeys={[...allowedGroupKeys]}
            customerId={id}
            hideInviteButton
            onUsageChange={handleUsageChange}
            embedded
            isInviteDialogOpen={isInviteDialogOpen}
            onInviteDialogOpenChange={setIsInviteDialogOpen}
          />
        </DetailCard>
      )}

      {unsavedDialog}
    </DetailPageLayout>
  );
}
